"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getInformation = exports.getStandardError = exports.estimateTheta = exports.EXTENDED_THETA_BOUNDS = exports.DEFAULT_THETA_BOUNDS = void 0;
const utilities_1 = require("./utilities");
const bayesModal_1 = require("./bayesModal");
const ratingScale_1 = require("./ratingScale");
/**
 * @typedef {import('./types').OptionalNumber} OptionalNumber
 */
/**
 * @typedef {import('./types').QuestionConfig} QuestionConfig
 */
/**
 * @typedef {import('./types').IRTConfig} IRTConfig
 */
/**
 * @typedef {import('./types').IRTParams} IRTParams
 */
/**
 * @typedef {import('./types').RSMParams} RSMParams
 */
/**
 * @typedef {import('./types').EngineParams} EngineParams
 */
/**
 * @typedef {import('./types').Response} Response
 */
/**
 * @typedef {readonly [number, number]} ThetaBounds
 */
/**
 * @typedef {'BM' | 'EAP' | 'ML'} EstimatorMethod
 */
/**
 * Default theta bounds for the optimizer.
 * Covers approximately 6 standard deviations which is sufficient for most clinical IRT applications.
 * @type {ThetaBounds}
 */
exports.DEFAULT_THETA_BOUNDS = ([-5, 5]);
/**
 * Extended theta bounds for extreme cases.
 * Use when patients may have traits outside the standard range.
 * @type {ThetaBounds}
 */
exports.EXTENDED_THETA_BOUNDS = ([-10, 10]);
// Prior precision = 1 / σ_prior²; σ_prior = 1 → precision = 1.
// Used in SE for BM/EAP per catR's semTheta() with method ∈ {BM, EAP}.
const PRIOR_PRECISION = 1;
/**
 * Detect whether a params map is an RSMParams shape (per-item { delta, tau, K,
 * scoreByValue }) vs the legacy GRM IRTParams shape (per-item { value → triple }).
 *
 * Checks every item — a mixed-shape map (some RSM, some GRM) is rejected as
 * non-RSM so the caller routes to the safer GRM path. The legacy GRM shape
 * is { qid: { value: [α, β1, β2] } } — `value` here is a numeric-string key
 * mapping to an array, so it cannot collide with the `{ delta, tau, ... }`
 * RSM shape.
 *
 * Acts as a TypeScript type predicate: `if (isRSMParams(p)) { ... }` narrows
 * `p` to `RSMParams` inside the block and to `IRTParams` outside it.
 *
 * @param {EngineParams} params
 * @returns {params is RSMParams}
 */
function isRSMParams(params) {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
        return false;
    }
    const keys = Object.keys(params);
    if (keys.length === 0)
        return false;
    for (let i = 0; i < keys.length; i += 1) {
        const v = params[keys[i]];
        if (!v ||
            typeof v !== 'object' ||
            Array.isArray(v) ||
            !('delta' in v) ||
            !('tau' in v) ||
            !Array.isArray(v.tau)) {
            return false;
        }
    }
    return true;
}
/**
 * Estimate θ under RSM via MLE. Uses differential evolution against the
 * RSM negative log-likelihood — same optimiser as the GRM ML path, with the
 * RSM probability function inside the likelihood.
 *
 * Responses with unknown questionIds, or response values whose score lookup
 * is missing, are filtered out (consistent with the GRM path).
 *
 * @param {Record<string, { delta: number, tau: number[], K: number, scoreByValue: Record<string, number> }>} rsmParams
 * @param {Response[]} responses
 * @returns {number}
 */
/**
 * Map a response to a valid RSM observation, or null if the response is not
 * usable (unknown item, missing score lookup, or non-finite score).
 *
 * @param {{ questionId: string, answer: { value: string } }} r
 * @param {Record<string, { delta: number, tau: number[], scoreByValue: Record<string, number> }>} rsmParams
 * @returns {{ delta: number, tau: number[], k: number } | null}
 */
function toRSMObservation(r, rsmParams) {
    if (!r || !r.answer)
        return null;
    const itemParams = rsmParams[r.questionId];
    if (!itemParams)
        return null;
    const k = itemParams.scoreByValue[r.answer.value];
    // Reject undefined, null, NaN, Infinity — none of these are valid category
    // indices. Distinguishes "I couldn't compute it" from "answer was 0".
    if (k === undefined || k === null || !Number.isFinite(k))
        return null;
    return { delta: itemParams.delta, tau: itemParams.tau, k };
}
/**
 * @param {RSMParams} rsmParams
 * @param {Response[]} responses
 * @param {number} [initialTheta]
 * @returns {number}
 */
function estimateThetaRsmMl(rsmParams, responses, initialTheta) {
    const observations = responses
        .map((r) => toRSMObservation(r, rsmParams))
        .filter(
    /** @returns {o is { delta: number, tau: number[], k: number }} */
    (o) => o !== null);
    if (observations.length === 0)
        return 0;
    // Newton-Raphson on the RSM log-likelihood using the analytical score
    // (observed minus expected) and Hessian (negative total Fisher info).
    // Bounded to [-5, 5] to match the legacy 2PL ML saturation behaviour.
    return (0, ratingScale_1.rsmMLE)(observations, {
        theta0: Number.isFinite(initialTheta) ? initialTheta : 0,
    });
}
/**
 * Fisher information under RSM for a single item at θ.
 *
 * @param {Record<string, { delta: number, tau: number[] }>} rsmParams
 * @param {number} theta
 * @param {string} questionId
 * @returns {number}
 */
function getInformationRSM(rsmParams, theta, questionId) {
    const item = rsmParams[questionId];
    if (!item)
        return 0;
    return (0, ratingScale_1.rsmFisherInformation)(Number.isFinite(theta) ? theta : 0, item.delta, item.tau);
}
/**
 * Standard error of θ̂ under RSM: SE = 1 / √(Σ_i I_i(θ̂)). For ML, no prior
 * precision is added.
 *
 * @param {Record<string, { delta: number, tau: number[] }>} rsmParams
 * @param {Response[]} responses
 * @param {number} theta
 * @returns {number}
 */
function getStandardErrorRSM(rsmParams, responses, theta) {
    let information = 0;
    responses.forEach((response) => {
        const item = rsmParams[response.questionId];
        if (item) {
            information += (0, ratingScale_1.rsmFisherInformation)(Number.isFinite(theta) ? theta : 0, item.delta, item.tau);
        }
    });
    return information === 0 ? 99999.9 : 1 / Math.sqrt(information);
}
/**
 * Estimate Theta. Dispatches to BM (Bayes Modal — catR/Promin default), EAP
 * (Expected A Posteriori — PROMIS default), or ML (Maximum Likelihood — legacy).
 *
 * @param {EngineParams} params
 * @param {Response[]} responses
 * @param {object | number | null} [optionsOrInitialTheta] - object with method, OR legacy initialTheta number, OR null
 * @returns {number}
 */
function estimateTheta(params, responses, optionsOrInitialTheta = null) {
    // Backwards-compat: 3rd arg used to be `initialTheta: number | null`.
    // New form: `{ method, initialTheta }`. Accept both.
    /** @type {{ method?: EstimatorMethod, initialTheta?: number | null }} */
    let options;
    if (optionsOrInitialTheta !== null &&
        typeof optionsOrInitialTheta === 'object') {
        options = optionsOrInitialTheta;
    }
    else {
        options = {
            initialTheta: /** @type {number | null} */ (optionsOrInitialTheta),
        };
    }
    // RSM dispatch — Andrich rating-scale (SHAW-CAT). Always MLE; method option
    // is ignored for RSM since Singapore has confirmed MLE end-to-end.
    if (isRSMParams(params)) {
        return estimateThetaRsmMl(params, responses, options.initialTheta == null ? 0 : options.initialTheta);
    }
    // GRM (2PL) path — PROMIS-style per-item-β calibrations.
    // Default 'BM' — Bayes Modal (catR/Promin convention). Regularizes θ via a
    // standard-normal prior so SE-based adaptive stopping actually fires on
    // real patient response patterns. ML and EAP available via { method: ... }.
    // ML is preferred only for QA on extreme test patterns (where it saturates
    // θ to ±5 to match Promin's clipped T=1/T=99 display).
    const method = options.method || 'BM';
    const initialTheta = options.initialTheta == null ? null : options.initialTheta;
    const validResponses = responses.filter(({ questionId, answer }) => {
        return params[questionId] && params[questionId][answer.value];
    });
    if (validResponses.length === 0) {
        if (initialTheta !== null &&
            initialTheta !== 0 &&
            Number.isFinite(initialTheta)) {
            return initialTheta;
        }
        return 0;
    }
    switch (method) {
        case 'BM':
            return (0, bayesModal_1.estimateThetaBM)(params, validResponses);
        case 'EAP':
            return (0, bayesModal_1.estimateThetaEAP)(params, validResponses);
        case 'ML': {
            // Newton-Raphson with grid fallback. Replaces the legacy DE-based MLE
            // (50k cost-fn evaluations per call) with ~10 Newton iterations plus a
            // 201-step grid scan when Newton doesn't converge. On uniform extreme
            // response patterns θ saturates at the [-5, +5] bound — matches
            // catR thetaEst(method="ML", range=c(-5,5)) Promin behaviour.
            return (0, bayesModal_1.estimateThetaML)(params, validResponses);
        }
        default:
            throw new Error(`Unknown estimator method: ${method}`);
    }
}
exports.estimateTheta = estimateTheta;
/**
 * Standard error of θ̂. For BM/EAP the prior contributes precision (1/σ²)
 * to the information sum (catR semTheta convention). For ML the prior term
 * is omitted (Fisher information only).
 *
 * @param {EngineParams} params
 * @param {Response[]} responses
 * @param {number} theta
 * @param {{ method?: EstimatorMethod }} [options]
 * @returns {number}
 */
function getStandardError(params, responses, theta, options = {}) {
    if (isRSMParams(params)) {
        return getStandardErrorRSM(params, responses, theta);
    }
    const method = options.method || 'BM';
    let information = 0;
    responses.forEach((response) => {
        const questionParams = params[response.questionId];
        if (questionParams) {
            information += (0, utilities_1.informationResponseFunctionForQuestion)(Number.isFinite(theta) ? theta : 0, questionParams);
        }
    });
    if (method === 'BM' || method === 'EAP') {
        information += PRIOR_PRECISION;
    }
    // review:manual: BM/EAP path with zero valid responses returns SE = 1.0
    // (= 1/√PRIOR_PRECISION, mathematically the prior std). The old sentinel
    // 99999.9 encoded "not measured yet" and served as defence-in-depth
    // against pathological configs: a CAT section with standardErrorThreshold
    // ≥ 1 and minItems = 0 now satisfies the SE stop condition BEFORE
    // administering any item. All in-repo sections use SE thresholds of
    // 0.3–0.5 so no live section trips this, but the sentinel was there
    // specifically to catch misconfigs. If restoring: early-return 99999.9
    // when responses.length === 0, before the PRIOR_PRECISION add.
    return information === 0 ? 99999.9 : 1 / Math.sqrt(information);
}
exports.getStandardError = getStandardError;
/**
 * Returns the information for a given question
 * @param {EngineParams} params
 * @param {number} theta
 * @param {string} questionId
 * @returns {number}
 */
function getInformation(params, theta, questionId) {
    if (isRSMParams(params)) {
        return getInformationRSM(params, theta, questionId);
    }
    const questionParams = params[questionId];
    if (!questionParams) {
        return 0;
    }
    const information = (0, utilities_1.informationResponseFunctionForQuestion)(Number.isFinite(theta) ? theta : 0, questionParams);
    return information;
}
exports.getInformation = getInformation;
//# sourceMappingURL=irtMethods.js.map